import * as React from 'react'

import {
  NodeMouseHandler,
  type OnEdgesChange,
  type OnNodesChange,
  ReactFlow,
  type Edge as ReactFlowEdge,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { TreeFragment } from 'generated/resources/graphql'

import Edge from 'modules/job_catalog/components/DevJobCatalogTree/components/Edge'
import Node from 'modules/job_catalog/components/DevJobCatalogTree/components/Node'
import { type CustomNode } from 'modules/job_catalog/components/DevJobCatalogTree/types'
import useExpandCollapse from 'modules/job_catalog/components/DevJobCatalogTree/useExpandCollapse'

import useTreeDataToNodes from './useTreeDataToNodes'

interface Props {
  width: number
  height: number
  treeData: TreeFragment
}

const JobCatalogTree = ({ width, height, treeData }: Props) => {
  const initialData = useTreeDataToNodes(treeData)
  const { fitView } = useReactFlow()
  const [nodes, setNodes] = React.useState<CustomNode[]>(initialData.nodes)
  const [edges, setEdges] = React.useState<ReactFlowEdge[]>(initialData.edges)
  const { nodes: visibleNodes, edges: visibleEdges } = useExpandCollapse(nodes, edges)

  const onNodesChange: OnNodesChange<CustomNode> = React.useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  )
  const onEdgesChange: OnEdgesChange = React.useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  )
  const onNodeClick: NodeMouseHandler = React.useCallback(
    (_, node) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            return {
              ...n,
              data: { ...n.data, expanded: !n.data.expanded },
            }
          }

          return n
        })
      )

      fitView({ duration: 300 })
    },
    [fitView]
  )

  const edgeTypes = {
    'custom-edge': Edge,
  }

  const nodeTypes = {
    'custom-node': Node,
  }

  return (
    <ReactFlow
      fitView
      nodes={visibleNodes}
      edges={visibleEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      width={width}
      height={height}
      minZoom={0.2}
      maxZoom={2}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={onNodeClick}
    />
  )
}

const DevJobCatalogTree = ({ width, height, treeData }: Props) => {
  return (
    <ReactFlowProvider>
      <JobCatalogTree width={width} height={height} treeData={treeData} />
    </ReactFlowProvider>
  )
}

export default DevJobCatalogTree
